// Tiny overview canvas sampling the terrain grid and overlaying tank dots
// in their owner colour. Reintroduced 2026-05-08 as part of the right-
// column work — the original Bolo had no minimap (so we deleted it during
// authentic-UI prep), but the alt mockup makes a deliberate UX divergence:
// the right column at 150px wide had wasted space, and modern wide
// displays make at-a-glance overview harder than it was on 1990s Mac
// monitors. See ~/.claude memory project_minimap_revisit.md for the
// decision history.
//
// Sized adaptively to the canvas's CSS box (the right-column slot drives
// it), so it fills its bevel without us hard-coding the dimension.
//
// Auto-zoom: maps are 256×256 but most have a small island surrounded by
// DeepSea, so a fixed full-grid sample wastes most of the canvas on dark
// blue. Instead we compute the bounding box of non-DeepSea tiles once per
// terrain change, pad it by 2 tiles, and letterbox the rendered region to
// preserve aspect on a square canvas. Interior water (rivers, lakes,
// inlets) stays visible because they aren't DeepSea.

import { MAP_SIZE, Terrain, TERRAIN_MASK, WORLD_UNITS_PER_TILE } from '@bolo2k/shared';
import type { ClientState } from './clientState.js';
import { ownerColor, terrainColor } from './colors.js';

// Pad the playable bbox by this many tiles so the coastline isn't pressed
// against the canvas edge. Two tiles is enough to read at a glance without
// wasting much canvas on water.
const BBOX_PAD_TILES = 2;

// Robust #rgb / #rrggbb parser (terrainColor's fallback is 3-digit '#222').
function hexToRgb(hex: string): [number, number, number] {
  let h = hex.slice(1);
  if (h.length === 3) h = h[0]! + h[0]! + h[1]! + h[1]! + h[2]! + h[2]!;
  const v = parseInt(h, 16);
  return [(v >> 16) & 0xff, (v >> 8) & 0xff, v & 0xff];
}

// Pack an opaque RGB colour into the host byte order expected when writing
// a Uint32 into an RGBA ImageData buffer (little-endian hosts read RGBA
// bytes as an ABGR word — going through a byte view keeps this portable to
// either endianness).
const packScratch = new ArrayBuffer(4);
const packBytes = new Uint8Array(packScratch);
const packWord = new Uint32Array(packScratch);
function packRgb(r: number, g: number, b: number): number {
  packBytes[0] = r;
  packBytes[1] = g;
  packBytes[2] = b;
  packBytes[3] = 255;
  return packWord[0]!;
}

const LETTERBOX_PIXEL = packRgb(0, 0, 0); // solid black

// Precomputed terrain-code → packed-pixel palette, built once from
// colors.ts. Replaces the old per-pixel terrainColor() + 3× parseInt string
// parsing in the paint loop (mmSize² string parses per terrain repaint).
const TERRAIN_PIXEL_LUT: Uint32Array = (() => {
  const lut = new Uint32Array(TERRAIN_MASK + 1);
  for (let code = 0; code < lut.length; code++) {
    const [r, g, b] = hexToRgb(terrainColor(code));
    lut[code] = packRgb(r, g, b);
  }
  return lut;
})();

interface Bbox { x: number; y: number; w: number; h: number }
interface Layout { scaledW: number; scaledH: number; offsetX: number; offsetY: number }

export class Minimap {
  private ctx: CanvasRenderingContext2D;
  private terrainImage: ImageData | null = null;
  private mmSize = 0;
  // Version gates — skip the whole draw when no snapshot arrived since last
  // paint, and skip the per-pixel terrain rebuild unless terrain actually
  // changed. Sentinel -1 forces the first frame to paint.
  private lastVersion = -1;
  private terrainPaintedVersion = -1;
  // Auto-zoom state. Bbox is in tile coords; layout is in canvas-pixel
  // coords (with letterbox offsets). Bbox depends on the *map* only —
  // nothing in the sim converts land↔DeepSea, so it's computed once per
  // welcome (keyed on terrain-array identity: initFromWelcome allocates a
  // fresh Uint8Array) rather than on every terrain-delta snapshot. Layout
  // depends on bbox AND mmSize, so it's recomputed on either change.
  private bbox: Bbox = { x: 0, y: 0, w: MAP_SIZE, h: MAP_SIZE };
  private bboxTerrainRef: Uint8Array | null = null;
  private layout: Layout = { scaledW: 0, scaledH: 0, offsetX: 0, offsetY: 0 };

  constructor(private canvas: HTMLCanvasElement) {
    const c = canvas.getContext('2d');
    if (!c) throw new Error('minimap 2d context unavailable');
    this.ctx = c;
    this.fitToBox();
    new ResizeObserver(() => this.fitToBox()).observe(canvas);
  }

  private fitToBox(): void {
    const rect = this.canvas.getBoundingClientRect();
    const size = Math.max(1, Math.floor(Math.min(rect.width, rect.height)));
    if (size === this.mmSize) return;
    this.mmSize = size;
    this.canvas.width = size;
    this.canvas.height = size;
    this.terrainImage = this.ctx.createImageData(size, size);
    // Buffer was just (re)allocated as zeroed pixels — force a terrain repaint
    // even if terrainVersion hasn't moved.
    this.terrainPaintedVersion = -1;
    this.computeLayout();
  }

  draw(state: ClientState): void {
    if (!this.terrainImage || this.mmSize === 0) return;
    if (state.version === this.lastVersion) return;
    if (state.terrain !== this.bboxTerrainRef) {
      // New map (fresh terrain array from initFromWelcome) — the only event
      // that can move the land/DeepSea boundary. Full invalidate.
      this.computeBoundingBox(state);
      this.bboxTerrainRef = state.terrain;
      this.computeLayout();
      this.terrainPaintedVersion = -1;
    }
    if (state.terrainVersion !== this.terrainPaintedVersion) {
      this.paintTerrain(state);
      this.terrainPaintedVersion = state.terrainVersion;
    }
    this.ctx.putImageData(this.terrainImage, 0, 0);
    this.paintTanks(state);
    this.lastVersion = state.version;
  }

  // O(MAP_SIZE²) scan, runs once per welcome (see bboxTerrainRef). Finds the
  // tightest rectangle that covers every non-DeepSea tile, then pads.
  private computeBoundingBox(state: ClientState): void {
    let minX = MAP_SIZE;
    let minY = MAP_SIZE;
    let maxX = -1;
    let maxY = -1;
    for (let y = 0; y < MAP_SIZE; y++) {
      const row = y * MAP_SIZE;
      for (let x = 0; x < MAP_SIZE; x++) {
        const code = state.terrain[row + x]! & TERRAIN_MASK;
        if (code === Terrain.DeepSea) continue;
        if (x < minX) minX = x;
        if (x > maxX) maxX = x;
        if (y < minY) minY = y;
        if (y > maxY) maxY = y;
      }
    }
    if (maxX < 0) {
      // All-DeepSea map — degenerate, fall back to full grid so we render
      // something instead of a divide-by-zero.
      this.bbox = { x: 0, y: 0, w: MAP_SIZE, h: MAP_SIZE };
      return;
    }
    const x = Math.max(0, minX - BBOX_PAD_TILES);
    const y = Math.max(0, minY - BBOX_PAD_TILES);
    const w = Math.min(MAP_SIZE, maxX + BBOX_PAD_TILES + 1) - x;
    const h = Math.min(MAP_SIZE, maxY + BBOX_PAD_TILES + 1) - y;
    this.bbox = { x, y, w, h };
  }

  // Map the bbox onto the square mmSize canvas while preserving aspect ratio.
  // Wide bbox → vertical letterbox; tall bbox → horizontal letterbox.
  private computeLayout(): void {
    if (this.mmSize === 0 || this.bbox.w === 0 || this.bbox.h === 0) return;
    const aspect = this.bbox.w / this.bbox.h;
    let scaledW: number;
    let scaledH: number;
    if (aspect >= 1) {
      scaledW = this.mmSize;
      scaledH = Math.max(1, Math.floor(this.mmSize / aspect));
    } else {
      scaledH = this.mmSize;
      scaledW = Math.max(1, Math.floor(this.mmSize * aspect));
    }
    this.layout = {
      scaledW,
      scaledH,
      offsetX: Math.floor((this.mmSize - scaledW) / 2),
      offsetY: Math.floor((this.mmSize - scaledH) / 2),
    };
  }

  private paintTerrain(state: ClientState): void {
    // Uint32 view over the RGBA buffer — one packed write per pixel from the
    // precomputed palette LUT instead of 4 byte writes + 3 string parses.
    const px = new Uint32Array(this.terrainImage!.data.buffer);
    const { scaledW, scaledH, offsetX, offsetY } = this.layout;
    const stepX = this.bbox.w / scaledW;
    const stepY = this.bbox.h / scaledH;
    // Letterbox region — solid black so the trimmed-off ocean reads as
    // "outside the map" rather than empty terrain. Filling everything first
    // and overwriting the bbox interior is cheaper than a per-pixel branch.
    px.fill(LETTERBOX_PIXEL);
    for (let y = 0; y < scaledH; y++) {
      const ty = this.bbox.y + ((y * stepY) | 0);
      const terrainRow = ty * MAP_SIZE;
      const outRow = (y + offsetY) * this.mmSize + offsetX;
      for (let x = 0; x < scaledW; x++) {
        const tx = this.bbox.x + ((x * stepX) | 0);
        px[outRow + x] = TERRAIN_PIXEL_LUT[state.terrain[terrainRow + tx]! & TERRAIN_MASK]!;
      }
    }
  }

  private paintTanks(state: ClientState): void {
    const { scaledW, scaledH, offsetX, offsetY } = this.layout;
    for (const t of state.tanks) {
      if (!t.alive) continue;
      const tileFx = t.x / WORLD_UNITS_PER_TILE;
      const tileFy = t.y / WORLD_UNITS_PER_TILE;
      const localX = tileFx - this.bbox.x;
      const localY = tileFy - this.bbox.y;
      // A tank outside the rendered bbox is unusual (would require driving
      // onto deep sea, which generally drowns) but skip cleanly if it
      // happens — better than drawing into the letterbox border.
      if (localX < 0 || localX > this.bbox.w) continue;
      if (localY < 0 || localY > this.bbox.h) continue;
      const cx = offsetX + (localX / this.bbox.w) * scaledW;
      const cy = offsetY + (localY / this.bbox.h) * scaledH;
      this.ctx.fillStyle = ownerColor(t.owner);
      this.ctx.fillRect((cx | 0) - 1, (cy | 0) - 1, 3, 3);
    }
  }
}
